export type KlipyGifResult = {
  id: string;
  title: string;
  url: string;
  previewUrl: string;
};

export const KLIPY_GIF_ALT = "KLIPY GIF";
export const KLIPY_GIF_TOKEN_PATTERN = /\[gif:([^\]\r\n]*)\]/g;

const TRUSTED_KLIPY_HOSTS = new Set(["klipy.com", "www.klipy.com", "api.klipy.com"]);
const TRUSTED_MEDIA_EXTENSIONS = new Set([".gif", ".webp", ".png", ".jpg", ".jpeg", ".mp4"]);

export function isTrustedKlipyMediaUrl(value: string) {
  try {
    const url = new URL(value);
    const hostname = url.hostname.toLowerCase();
    if (url.protocol !== "https:") return false;
    if (!TRUSTED_KLIPY_HOSTS.has(hostname) && !hostname.endsWith(".klipy.com")) return false;
    const pathname = url.pathname.toLowerCase();
    return [...TRUSTED_MEDIA_EXTENSIONS].some((extension) => pathname.endsWith(extension));
  } catch {
    return false;
  }
}

export function klipyGifToken(url: string) {
  return isTrustedKlipyMediaUrl(url) ? `[gif:${url}]` : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function readString(value: unknown) {
  return typeof value === "string" ? value : null;
}

function collectTrustedMediaUrls(value: unknown, urls: string[] = []) {
  if (typeof value === "string" && isTrustedKlipyMediaUrl(value)) {
    urls.push(value);
    return urls;
  }

  if (Array.isArray(value)) {
    for (const item of value) collectTrustedMediaUrls(item, urls);
    return urls;
  }

  if (isRecord(value)) {
    for (const item of Object.values(value)) collectTrustedMediaUrls(item, urls);
  }

  return urls;
}

function preferredUrlFromKeys(item: Record<string, unknown>, keys: string[]) {
  for (const key of keys) {
    const value = item[key];
    if (typeof value === "string" && isTrustedKlipyMediaUrl(value)) return value;
    const nestedUrls = collectTrustedMediaUrls(value);
    if (nestedUrls[0]) return nestedUrls[0];
  }
  return null;
}

function normalizeKlipyItem(item: unknown, index: number): KlipyGifResult | null {
  if (!isRecord(item)) return null;

  const url =
    preferredUrlFromKeys(item, ["gif", "gif_url", "url", "file", "media", "media_formats", "formats", "assets", "images"]) ??
    collectTrustedMediaUrls(item).find((candidate) => candidate.toLowerCase().includes(".gif")) ??
    collectTrustedMediaUrls(item)[0] ??
    null;

  if (!url) return null;

  const previewUrl =
    preferredUrlFromKeys(item, ["preview", "preview_url", "thumbnail", "thumbnail_url", "tinygif", "nanogif", "media", "media_formats"]) ??
    url;
  const id = readString(item.id) ?? readString(item.slug) ?? readString(item.content_id) ?? `klipy-${index}`;
  const title = readString(item.title) ?? readString(item.name) ?? readString(item.content_description) ?? "KLIPY GIF";

  return {
    id,
    title,
    url,
    previewUrl,
  };
}

export function normalizeKlipySearchResponse(payload: unknown) {
  const candidates = isRecord(payload)
    ? payload.data && isRecord(payload.data) && Array.isArray(payload.data.data)
      ? payload.data.data
      : payload.data && isRecord(payload.data) && Array.isArray(payload.data.results)
        ? payload.data.results
        : Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload.results)
            ? payload.results
            : []
    : [];

  const seen = new Set<string>();
  const results: KlipyGifResult[] = [];
  for (const [index, item] of candidates.entries()) {
    const normalized = normalizeKlipyItem(item, index);
    if (!normalized || seen.has(normalized.url)) continue;
    seen.add(normalized.url);
    results.push(normalized);
    if (results.length >= 12) break;
  }

  return results;
}
